<template>
  <v-container>
    <v-row justify="center">
      <v-col
        cols="12"
        md="9"
      >
        <v-form ref="form">
          <v-row>
            <v-col
              cols="11"
              class="d-flex align-center"
            >
              <div
                class="d-flex align-center w-100"
                @dragover.prevent="onDragOver"
                @drop.prevent="onDrop"
              >
                <v-file-input
                  v-model="selectedFile"
                  :label="$t('gpt.label.fileUpload')"
                  prependIcon="mdi-paperclip"
                  outlined
                  dense
                  hideDetails
                  @change="onFileSelected"
                />
              </div>
            </v-col>
            <v-col
              cols="1"
              class="d-flex align-center"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <div>
                  <ul>
                    <li>{{ $t('gpt.fileInformation.supportedFormat') }}</li>
                    <li>{{ $t('gpt.fileInformation.excelFormat') }}</li>
                    <li>{{ $t('gpt.fileInformation.wordFormat') }}</li>
                    <li>{{ $t('gpt.fileInformation.sizeLimit') }}</li>
                  </ul>
                </div>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-checkbox
            v-if="!forPublicDoc"
            v-model="isPrivate"
            :label="$t('gpt.label.private')"
            class="mt-3"
          />
          <v-btn
            :disabled="!selectedFile || uploadError !== false || uploadStarted"
            color="primary"
            @click="uploadFile"
          >
            {{ $t('gpt.label.upload') }}
          </v-btn>
        </v-form>
        <v-progress-linear
          v-if="uploadStarted"
          v-model="progress"
          height="10"
          :color="uploadError ? 'red' : 'primary'"
          :bufferValue="progress"
          stream
          striped
          class="mt-4 mb-4"
        >
          <template v-slot:default="{ value }">
            <strong>{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
        <v-alert
          v-if="uploadSuccess"
          type="success"
        >
          {{ $t('gpt.label.succes') }}
        </v-alert>
        <v-alert
          v-if="uploadError"
          type="error"
        >
          {{ uploadError }}
        </v-alert>
        <v-text-field
          v-model="search"
          appendIcon="mdi-magnify"
          :label="$t('gpt.label.search')"
          singleLine
          hideDetails
          class="mt-4"
        />
        <v-data-table
          :headers="headers"
          :items="documentType"
          :search="search"
          class="elevation-1 mt-4"
          :footerProps="{
            itemsPerPageText: $t('features.footers.rowPerPage')
          }"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-center">
              <v-icon
                small
                class="mr-2"
                @click="confirmDelete(item.name)"
              >
                mdi-delete
              </v-icon>
            </div>
          </template>
        </v-data-table>
        <v-dialog
          v-model="dialog"
          maxWidth="500px"
        >
          <v-card>
            <v-card-text class="font-weight-bold ms-1 mb-2">
              {{ $t('gpt.label.deleteConfirmation') }} "{{ documentToDelete }}"?
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="#26394b"
                text
                @click="closeDialog"
              >
                {{ $t('gpt.label.cancel') }}
              </v-btn>
              <v-btn
                color="#26394b"
                class="button"
                @click="deleteDocument"
              >
                {{ $t('gpt.label.delete') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Mutations from '@/helpers/graphql/mutations';
import Subscriptions from '@/helpers/graphql/subscriptions';
import GraphQLSubscriptionClient from '@/helpers/graphql/graphql_subscription_client';
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from 'uuid';

export default {
    props: {
        forPublicDoc: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            maxFileSizeMB: 25,
            documents: [],
            privateDocuments: [],
            publicDocuments: [],
            selectedFile: null,
            uploadSuccess: false,
            uploadError: null,
            uploadStarted: false,
            progress: 0,
            isPrivate: false,
            webSocket: null,
            embeddings_model: {
                modelId: 'amazon.titan-embed-text-v2:0',
                provider: 'Bedrock',
            },
            dialog: false,
            documentToDelete: null,
            search: '',
            userTeam: '',
        };
    },
    computed: {
        documentType() {
            if (this.forPublicDoc) {
                return this.documents;
            } if (this.isPrivate) {
                return this.privateDocuments;
            }
            return this.publicDocuments;
        },
        headers() {
            return [
                { text: this.$t('gpt.label.uploadedDocuments'), value: 'name' },
                { text: this.$t('gpt.label.actions'), value: 'actions', align: 'center', sortable: false },
            ];
        },
    },

    mounted() {
        this.userTeam = JSON.parse(
            window.localStorage.getItem('aquassist'),
        ).team.team.name;
        this.webSocket = new GraphQLSubscriptionClient(
            process.env.VUE_APP_GRAPHQL_ENDPOINT,
            window.$cookies.get('accessToken'),
        );
        this.getDocuments();
    },

    methods: {
        confirmDelete(name) {
            this.documentToDelete = name;
            this.dialog = true;
        },
        closeDialog() {
            this.dialog = false;
            this.documentToDelete = null;
        },
        async deleteDocument() {
            try {
                await this.$rq.deleteGptDocument(
                    this.documentToDelete,
                    this.getFolder(),
                );
            } catch (e) {
                this.uploadSuccess = false;
                this.uploadError = e;
                this.error = e;
            }
            this.getDocuments();
            this.closeDialog();
        },

        async getDocuments() {
            if (this.forPublicDoc) {
                this.documents = this.handleDocuments(
                    await this.$rq.listGptDocuments('public'),
                    'public',
                );
            } else {
                const privateFolder = `${this.getCompany() }_private`;
                const publicFolder = `${this.getCompany() }_public`;
                this.privateDocuments = this.handleDocuments(
                    await this.$rq.listGptDocuments(privateFolder),
                    privateFolder,
                );
                this.publicDocuments = this.handleDocuments(
                    await this.$rq.listGptDocuments(publicFolder),
                    publicFolder,
                );
            }
        },
        handleDocuments(doc, folder) {
            return doc.data.documents.map(d => ({
                name: d.replace(`${folder }/`, ''),
            }));
        },
        handleIngestionJobStatus(message) {
            const { status } = message.payload.data.updateIngestionJobStatus.files[0];
            const errors = [
                'File already exists',
                'Unable to load document',
                'Unsupported',
            ];
            if (errors.includes(status)) {
                this.uploadSuccess = false;
                this.uploadError = this.$t(`gpt.${ status}`);
                this.uploadStarted = false;
                this.webSocket.unsubscribe();
            } else if (status === 'File transformed') {
                this.progress = 66;
            } else if (status === 'Supported') {
                this.isSupported = true;
                this.progress = 33;
            } else if (status === 'Ingested') {
                this.isSupported = true;
                this.progress = 100;
                this.uploadSuccess = true;
                this.getDocuments();
                this.webSocket.unsubscribe();
                this.uploadStarted = false;
            }
        },
        bitesToMB(bites) {
            return bites / (1024 * 1024);
        },
        onFileSelected(file) {
            this.uploadSuccess = false;
            this.progress = 0;
            if (!file) {
                this.selectedFile = null;
                this.uploadError = false;
                this.isSupported = null;
                return;
            }
            const fileSizeMB = this.bitesToMB(file.size);
            this.selectedFile = file;
            this.uploadStarted = false;

            if (fileSizeMB > this.maxFileSizeMB) {
                this.uploadError = this.$t('gpt.fileToLarge');
            } else {
                this.uploadError = false;
            }

            this.isSupported = null;
        },
        onDragOver(event) {
            event.preventDefault();
            const { dataTransfer } = event;
            dataTransfer.dropEffect = 'copy';
        },

        onDrop(event) {
            const [file] = event.dataTransfer.files;
            if (file) {
                this.selectedFile = file;
                this.$refs.fileInput.$refs.input.files = [file];
            }
        },
        async uploadFile() {
            if (!this.selectedFile) return;
            this.uploadStarted = true;
            const ingestionjobid = uuidv4().substring(0, 10);
            this.webSocket.subscribe(
                Subscriptions.UPDATE_INGESTION_JOB_STATUS,
                'UpdateIngestionJobStatus',
                {
                    ingestionjobid,
                },
                this.handleIngestionJobStatus,
            );
            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append(
                'payload',
                JSON.stringify({
                    publicFile: this.forPublicDoc,
                    company: this.getFolder(),
                }),
            );
            await this.$rq.uploadGptDocumentation(null, formData);
            await this.ingestDocument(ingestionjobid);
        },
        getCompany() {
            return this.userTeam.toLowerCase().replace(/[,.\s]/g, '');
        },
        getSuffix() {
            return `_${ this.isPrivate ? 'private' : 'public'}`;
        },
        getFolder() {
            return this.forPublicDoc ? 'public' : this.getCompany() + this.getSuffix();
        },
        async ingestDocument(ingestionjobid) {
            try {
                const variables = {
                    ingestionInput: {
                        ingestionjobid,
                        embeddings_model: this.embeddings_model,
                        files: [{ status: '', name: this.selectedFile.name }],
                        ignore_existing: false,
                        for_public: this.forPublicDoc,
                        company: this.getFolder(),
                    },
                };
                await this.$apollo.mutate({
                    mutation: Mutations.INGEST_DOCUMENTS,
                    variables,
                });
            } catch (e) {
                this.uploadSuccess = false;
                this.uploadError = e;
                this.webSocket.unsubscribe();
                this.error = e;
                this.uploadStarted = false;
            }
        },
    },
};
</script>

<style scoped>
.v-container {
  max-width: 600px;
  margin: 0 auto;
  padding-top: 20px;
}
.mr-2 {
  margin-right: 8px;
}
.d-flex {
  display: flex;
}
.justify-center {
  justify-content: center;
}
.button{
 color: #fff;
}
</style>
